import { useState } from 'react';

import { Badge } from '~/components/ui/badge';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { cn } from '~/utils/cn';

import { useProductTranslations } from '../../hooks/use-translations';
import type { LocalizationQaStatus, LocalizedContent } from '../../types/translations.types';

const QA_STYLE: Record<LocalizationQaStatus, string> = {
  PASS: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  FLAG: 'border-amber-200 bg-amber-50 text-amber-700',
  FAIL: 'border-red-200 bg-red-50 text-red-700',
};

const QA_LABEL: Record<LocalizationQaStatus, string> = {
  PASS: 'QA pass',
  FLAG: 'QA flag',
  FAIL: 'Held back',
};

interface ProductTranslationsSectionProps {
  /** Any id of the product's run; workflow-service resolves the scope. */
  workflowId: string;
  productId: string;
  enabled: boolean;
}

/**
 * The product's content in every translated locale, one tab per locale. Internal users only.
 * Renders nothing until the product has at least one locale, so an English-only run shows no
 * empty section.
 */
export function ProductTranslationsSection({
  workflowId,
  productId,
  enabled,
}: ProductTranslationsSectionProps) {
  const canTranslate = useCanTranslateContent();
  const { data } = useProductTranslations(workflowId, productId, enabled && canTranslate);
  const [selected, setSelected] = useState<string | null>(null);
  if (!canTranslate || !data || data.length === 0) return null;

  const key = (c: LocalizedContent) => `${c.locale}-${c.enrichmentPhase ?? ''}`;
  const active = data.find((c) => key(c) === selected) ?? data[0];
  if (!active) return null;
  const fields = active.fields ?? {};
  const highlights = fields.product_highlight ?? [];
  const details = fields.product_detail ?? [];

  return (
    <div className="border-t" data-testid="translations-product-section">
      <h3
        className="text-muted-foreground px-5 pt-4 pb-2 text-[11px] font-semibold tracking-wide uppercase"
        data-testid="translations-product-title"
      >
        Translations
      </h3>
      <div className="flex flex-wrap gap-1.5 px-5 pb-3" data-testid="translations-product-tabs">
        {data.map((c) => (
          <button
            key={key(c)}
            data-testid={`translations-product-tabs-${c.locale}${c.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}`}
            type="button"
            onClick={() => {
              setSelected(key(c));
            }}
            className={cn(
              'cursor-pointer rounded-md border px-2 py-0.5 text-xs',
              key(c) === key(active) ? 'bg-selected-row text-muted-blue' : 'text-muted-foreground',
            )}
          >
            {c.locale}
            {c.enrichmentPhase === 'SAMPLE' && ' (sample)'}
          </button>
        ))}
      </div>

      <div className="space-y-3 px-5 pb-5 text-sm" data-testid="translations-product-content">
        <div className="flex flex-wrap items-center gap-1.5">
          {active.qaStatus && (
            <Badge
              variant="outline"
              className={cn('rounded-md text-[11px]', QA_STYLE[active.qaStatus])}
              data-testid="translations-product-qa-badge"
            >
              {QA_LABEL[active.qaStatus]}
            </Badge>
          )}
          {active.stale && (
            <Badge
              variant="outline"
              className="rounded-md border-amber-200 bg-amber-50 text-[11px] text-amber-700"
              data-testid="translations-product-stale-badge"
            >
              Stale — English changed since
            </Badge>
          )}
          {active.model && (
            <span className="text-muted-foreground text-[11px]">{active.model}</span>
          )}
          {active.promptVersion && (
            <span className="text-muted-foreground text-[11px]">prompt {active.promptVersion}</span>
          )}
        </div>

        {active.qaStatus === 'FAIL' && (active.qaReasons?.length ?? 0) > 0 && (
          <ul
            className="text-destructive list-disc pl-5 text-xs"
            data-testid="translations-product-qa-reasons-list"
          >
            {active.qaReasons?.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}

        {fields.title && (
          <Row label="Title" value={fields.title} testId="translations-product-field-title" />
        )}
        {fields.short_title && (
          <Row
            label="Short title"
            value={fields.short_title}
            testId="translations-product-field-short-title"
          />
        )}
        {fields.description && (
          <Row
            label="Description"
            value={fields.description}
            clamp
            testId="translations-product-field-description"
          />
        )}
        {highlights.length > 0 && (
          <div data-testid="translations-product-field-highlights">
            <div className="text-muted-foreground text-xs">Highlights</div>
            <ul className="list-disc pl-5">
              {highlights.map((h, i) => (
                <li key={`${String(i)}-${h}`}>{h}</li>
              ))}
            </ul>
          </div>
        )}
        {details.length > 0 && (
          <div data-testid="translations-product-field-details">
            <div className="text-muted-foreground text-xs">Product details</div>
            <ul className="space-y-0.5">
              {details.map((d, i) => (
                <li key={`${String(i)}-${d.attribute_name}`}>
                  <span className="text-muted-foreground">{d.attribute_name}:</span>{' '}
                  {d.attribute_value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  clamp,
  testId,
}: {
  label: string;
  value: string;
  clamp?: boolean;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <div className="text-muted-foreground text-xs">{label}</div>
      <p
        className={cn('whitespace-pre-line', clamp && 'line-clamp-6')}
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
    </div>
  );
}
